import { registerLocaleData, CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  LOCALE_ID,
  DestroyRef,
} from '@angular/core';
import esCo from '@angular/common/locales/es-CO';
import { PageTitleService } from '../../shared/services';
import { Product } from '../../shared/interfaces';
import { ProductAdd } from './product-add/product-add';
import { Modal } from '../../shared/components/modal/modal';
import { ProductsService } from '../../services/products.service';
import { AuthService } from '../../services/auth';
import { PreloaderService } from '../../shared/services/preloader.service';
import { ToastService } from '../../shared/services';
import { debounceTime, distinctUntilChanged, Subject, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
registerLocaleData(esCo);

const currencyFormatter = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

@Component({
  selector: 'app-inventory',
  imports: [CommonModule, Modal],
  templateUrl: './inventory.html',
  styleUrl: './inventory.css',
  providers: [{ provide: LOCALE_ID, useValue: 'es-CO' }],
})
export class Inventory implements OnInit {
  products: Product[] = [];
  loading = false;
  page = 1;
  pageSize = 20;
  totalCount = 0;
  searchTerm = '';
  totalPages = 1;
  titleService = inject(PageTitleService);
  productsService = inject(ProductsService);
  authService = inject(AuthService);
  preloader = inject(PreloaderService);
  toastService = inject(ToastService);
  ProductAddForm = ProductAdd;
  modalTitle = 'Crear producto';
  confirmButtonText = 'Guardar producto';
  modalInputs: Record<string, unknown> = {};
  open = false;
  isSaving = false;
  private destroyRef = inject(DestroyRef);
  private search$ = new Subject<string>();

  constructor() {
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((q) => {
          this.searchTerm = q;
          this.page = 1;
          this.load();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onSearch = (q: string) => this.search$.next(q);

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Productos',
      subtitle: 'Lo que vendes en tu negocio y cuánto te queda en inventario',
    });
    this.load();
  }

  // Stock shelf: 5 slots, one per ~6 units; low and out-of-stock change color
  readonly shelfSlots = Array(5).fill(0);
  readonly lowStockThreshold = 5;
  skeletonRows = Array(8).fill(0);

  stockState(product: Product): 'out' | 'low' | 'ok' {
    if (!product.stock || product.stock <= 0) return 'out';
    if (product.stock <= this.lowStockThreshold) return 'low';
    return 'ok';
  }

  shelfFill(product: Product): number {
    if (!product.stock || product.stock <= 0) return 0;
    return Math.min(this.shelfSlots.length, Math.ceil(product.stock / 6));
  }

  /** Markup over cost, same formula as the product form */
  marginPercent(product: Product): number | null {
    const cost = Number(product.cost);
    const price = Number(product.price);
    if (!cost || cost <= 0 || !price || price <= 0) return null;
    return ((price - cost) / cost) * 100;
  }

  formatPrice(value: number | string | null | undefined): string {
    const amount = typeof value === 'string' ? Number(value) : value;
    return currencyFormatter.format(amount ?? 0);
  }

  onPageChange = (p: number) => {
    if (p < 1 || p > this.totalPages || p === this.page) return;
    this.page = p;
    this.load();
  };

  load() {
    this.loading = true;
    this.preloader.show();
    this.productsService
      .getProducts({
        page: this.page,
        pageSize: this.pageSize,
        searchTerm: this.searchTerm || undefined,
        isActive: undefined,
      })
      .subscribe({
        next: (res) => {
          this.products = res.products;
          this.page = res.page;
          this.pageSize = res.pageSize;
          this.totalCount = res.totalCount;
          this.totalPages = res.totalPages;
          this.loading = false;
          this.preloader.hide();
        },
        error: (err) => {
          console.error(err);
          this.preloader.hide();
          this.loading = false;
          this.toastService.show('No se pudieron cargar los productos. Recarga la página.', 'error');
        },
      });
  }

  onCreateProduct() {
    this.modalTitle = 'Crear producto';
    this.confirmButtonText = 'Guardar producto';
    this.modalInputs = {
      entityId: null,
    };
    this.open = true;
  }

  onEditProduct(productId: string) {
    this.modalTitle = 'Editar producto';
    this.confirmButtonText = 'Guardar cambios';
    this.modalInputs = {
      entityId: productId,
    };
    this.open = true;
  }

  onModalConfirm(payload: Product | any): void {
    this.isSaving = true;
    if (payload.productId) {
      // Update
      this.productsService
        .updateProduct(payload.productId, {
          name: payload.name,
          description: payload.description,
          sku: payload.sku,
          category: payload.category,
          cost: payload.cost,
          price: payload.price,
          stock: payload.stock,
          isActive: payload.isActive,
        })
        .subscribe({
          next: () => {
            this.toastService.show('Producto actualizado', 'success');
            this.open = false;
            this.isSaving = false;
            this.load();
          },
          error: (err) => {
            this.isSaving = false;
            const apiError = (err as any)?.error;
            const errors = Array.isArray(apiError?.errors) ? apiError.errors : null;
            const message =
              (errors && errors.length ? errors.join('<br>') : apiError?.message) ??
              'Error al actualizar el producto';
            this.toastService.show(message, 'error', 6000);
          },
        });
    } else {
      // Create
      this.productsService
        .createProduct({
          name: payload.name,
          description: payload.description,
          sku: payload.sku,
          category: payload.category,
          cost: payload.cost,
          price: payload.price,
          stock: payload.stock,
        })
        .subscribe({
          next: () => {
            this.toastService.show('Producto creado', 'success');
            this.open = false;
            this.isSaving = false;
            this.load();
          },
          error: (err) => {
            this.isSaving = false;
            const apiError = (err as any)?.error;
            const errors = Array.isArray(apiError?.errors) ? apiError.errors : null;
            const message =
              (errors && errors.length ? errors.join('<br>') : apiError?.message) ??
              'Error al crear el producto';
            this.toastService.show(message, 'error', 6000);
          },
        });
    }
  }


  downloadTemplate(): void {
    this.productsService.downloadTemplate().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'products-template.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.toastService.show('Plantilla descargada', 'success');
      },
      error: (err) => {
        console.error(err);
        this.toastService.show('No se pudo descargar la plantilla. Inténtalo de nuevo.', 'error');
      },
    });
  }

  onImportFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.isSaving = true;
    this.preloader.show();

    this.productsService.importProducts(file).subscribe({
      next: (result) => {
        this.isSaving = false;
        this.preloader.hide();

        let message = `Importación lista: ${result.createdCount} creados, ${result.updatedCount} actualizados`;
        if (result.errorCount > 0) {
          message += `, ${result.errorCount} con errores`;
        }

        if (result.errors.length > 0) {
          const errorDetails = result.errors
            .map((e) => `Fila ${e.rowNumber} (${e.field}): ${e.message}`)
            .join('\n');
          console.warn('Errores de importación:', errorDetails);
          const rows = result.errors.slice(0, 3).map((e) => e.rowNumber).join(', ');
          message += `. Corrige ${result.errors.length > 3 ? 'las filas ' + rows + ' y otras' : 'la fila ' + rows} en el archivo y vuelve a importarlo.`;
        }

        this.toastService.show(message, result.errorCount > 0 ? 'warning' : 'success', 6000);
        this.load();
        input.value = ''; // Reset file input
      },
      error: (err) => {
        console.error(err);
        this.isSaving = false;
        this.preloader.hide();
        const apiError = (err as any)?.error;
        const message = apiError?.message ?? 'Error al importar productos';
        this.toastService.show(message, 'error', 6000);
        input.value = ''; // Reset file input
      },
    });
  }

}
